import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent, CardActions,
  Button, Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Tab, Tabs, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel,
  Divider, Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import api from '../axios';
import Footer from '../components/Footer';

const DIAS_SEMANA = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
const DIAS_LABEL = { domingo: 'Domingo', segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado' };
const STATUS_COLOR = { disponivel: 'info', confirmado: 'success', liberado: 'default' };
const STATUS_LABEL = { disponivel: 'Disponível', confirmado: 'Confirmado', liberado: 'Liberado' };

// ─── Componentes auxiliares ─────────────────────────────────────────────────

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function ConfirmDialog({ open, titulo, mensagem, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent><Typography>{mensagem}</Typography></DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button onClick={onConfirm} color="error" variant="contained">Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Aba: Departamentos ──────────────────────────────────────────────────────

function TabDepartamentos({ departamentos, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: '', descricao: '', cor: '#6B7280' });
  const [editId, setEditId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const abrirNovo = () => { setForm({ nome: '', descricao: '', cor: '#6B7280' }); setEditId(null); setErro(''); setOpen(true); };
  const abrirEditar = (d) => { setForm({ nome: d.nome, descricao: d.descricao || '', cor: d.cor || '#6B7280' }); setEditId(d.id); setErro(''); setOpen(true); };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/voluntariado/departamentos/${editId}`, form);
      else await api.post('/voluntariado/departamentos', form);
      setOpen(false);
      onRefresh();
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Erro ao salvar.');
    } finally { setSaving(false); }
  };

  const deletar = async (id) => {
    try { await api.delete(`/voluntariado/departamentos/${id}`); onRefresh(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro ao deletar.'); }
    setConfirmDel(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Departamentos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>Novo departamento</Button>
      </Box>

      <Grid container spacing={2}>
        {departamentos.map((d) => (
          <Grid item xs={12} sm={6} md={4} key={d.id}>
            <Card elevation={2} sx={{ borderRadius: 2, borderLeft: `6px solid ${d.cor}` }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{d.nome}</Typography>
                {d.descricao && <Typography variant="body2" color="text.secondary">{d.descricao}</Typography>}
                <Chip label={`${d.total_eventos} evento(s)`} size="small" sx={{ mt: 1 }} />
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
                <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Deletar"><IconButton size="small" color="error" onClick={() => setConfirmDel(d)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {departamentos.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">Nenhum departamento cadastrado ainda.</Alert>
          </Grid>
        )}
      </Grid>

      {/* Dialog form */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Editar departamento' : 'Novo departamento'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField label="Nome *" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} fullWidth />
          <TextField label="Descrição" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} fullWidth multiline rows={2} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Cor de identificação:</Typography>
            <input type="color" value={form.cor} onChange={e => setForm(f => ({ ...f, cor: e.target.value }))} style={{ width: 48, height: 36, cursor: 'pointer', border: 'none', borderRadius: 4 }} />
            <Chip label={form.cor} size="small" sx={{ bgcolor: form.cor, color: '#fff', fontWeight: 700 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={salvar} variant="contained" disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        titulo="Deletar departamento"
        mensagem={`Deletar "${confirmDel?.nome}"? Todos os eventos vinculados também serão removidos.`}
        onConfirm={() => deletar(confirmDel.id)}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}

// ─── Aba: Agenda ─────────────────────────────────────────────────────────────

function TabAgenda({ departamentos, onAbrirEscala }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroDepto, setFiltroDepto] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titulo: '', descricao: '', departamento_id: '', data: '', dia_semana: '', recorrente: false, hora_inicio: '', hora_fim: '', vagas_maximas: '' });
  const [editId, setEditId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroDepto ? { departamento_id: filtroDepto } : {};
      const res = await api.get('/voluntariado/eventos', { params });
      setEventos(res.data);
    } catch { setEventos([]); } finally { setLoading(false); }
  }, [filtroDepto]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => {
    setForm({ titulo: '', descricao: '', departamento_id: departamentos[0]?.id || '', data: '', dia_semana: '', recorrente: false, hora_inicio: '', hora_fim: '', vagas_maximas: '' });
    setEditId(null); setErro(''); setOpen(true);
  };

  const abrirEditar = (e) => {
    setForm({
      titulo: e.titulo, descricao: e.descricao || '', departamento_id: e.departamento_id,
      data: e.data || '', dia_semana: e.dia_semana || '', recorrente: e.recorrente,
      hora_inicio: e.hora_inicio || '', hora_fim: e.hora_fim || '',
      vagas_maximas: e.vagas_maximas != null ? String(e.vagas_maximas) : '',
    });
    setEditId(e.id); setErro(''); setOpen(true);
  };

  const salvar = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    if (!form.departamento_id) { setErro('Selecione um departamento.'); return; }
    if (!form.recorrente && !form.data) { setErro('Informe a data para evento único.'); return; }
    if (form.recorrente && !form.dia_semana) { setErro('Informe o dia da semana para evento recorrente.'); return; }
    setSaving(true);
    const payload = {
      ...form,
      vagas_maximas: form.vagas_maximas ? parseInt(form.vagas_maximas, 10) : null,
      data: form.recorrente ? null : (form.data || null),
      dia_semana: form.recorrente ? form.dia_semana : null,
    };
    try {
      if (editId) await api.put(`/voluntariado/eventos/${editId}`, payload);
      else await api.post('/voluntariado/eventos', payload);
      setOpen(false);
      carregar();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro ao salvar.'); }
    finally { setSaving(false); }
  };

  const deletar = async (id) => {
    try { await api.delete(`/voluntariado/eventos/${id}`); carregar(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro.'); }
    setConfirmDel(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Agenda de Eventos</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Departamento</InputLabel>
            <Select value={filtroDepto} label="Departamento" onChange={e => setFiltroDepto(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo} disabled={departamentos.length === 0}>Novo evento</Button>
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Evento</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Departamento</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Quando</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Horário</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Vagas</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Escala</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {eventos.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.titulo}</Typography>
                    {e.descricao && <Typography variant="caption" color="text.secondary">{e.descricao}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip label={e.departamento_nome} size="small" sx={{ bgcolor: e.departamento_cor, color: '#fff', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    {e.recorrente
                      ? <Chip label={`Todo ${DIAS_LABEL[e.dia_semana] || e.dia_semana}`} size="small" variant="outlined" />
                      : <Typography variant="body2">{e.data ? new Date(e.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</Typography>
                    }
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {e.hora_inicio && e.hora_fim ? `${e.hora_inicio} – ${e.hora_fim}` : e.hora_inicio || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {e.vagas_maximas
                      ? <Typography variant="body2">{e.confirmados}/{e.vagas_maximas}</Typography>
                      : <Typography variant="body2" color="text.secondary">{e.total_escalados} escalado(s)</Typography>
                    }
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" startIcon={<AssignmentIcon />} onClick={() => onAbrirEscala(e)}>
                      Escala
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(e)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Deletar"><IconButton size="small" color="error" onClick={() => setConfirmDel(e)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {eventos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Alert severity="info" sx={{ my: 1 }}>Nenhum evento cadastrado.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog form */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Editar evento' : 'Novo evento'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField label="Título *" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} fullWidth />
          <TextField label="Descrição" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} fullWidth multiline rows={2} />
          <FormControl fullWidth>
            <InputLabel>Departamento *</InputLabel>
            <Select value={form.departamento_id} label="Departamento *" onChange={e => setForm(f => ({ ...f, departamento_id: e.target.value }))}>
              {departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2">Tipo:</Typography>
            <Button variant={!form.recorrente ? 'contained' : 'outlined'} size="small" onClick={() => setForm(f => ({ ...f, recorrente: false }))}>Data única</Button>
            <Button variant={form.recorrente ? 'contained' : 'outlined'} size="small" onClick={() => setForm(f => ({ ...f, recorrente: true }))}>Recorrente (semanal)</Button>
          </Box>

          {form.recorrente ? (
            <FormControl fullWidth>
              <InputLabel>Dia da semana *</InputLabel>
              <Select value={form.dia_semana} label="Dia da semana *" onChange={e => setForm(f => ({ ...f, dia_semana: e.target.value }))}>
                {DIAS_SEMANA.map(d => <MenuItem key={d} value={d}>{DIAS_LABEL[d]}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <TextField label="Data *" type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Horário início" type="time" value={form.hora_inicio} onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Horário fim" type="time" value={form.hora_fim} onChange={e => setForm(f => ({ ...f, hora_fim: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
          <TextField label="Vagas máximas (deixe em branco para ilimitado)" type="number" value={form.vagas_maximas} onChange={e => setForm(f => ({ ...f, vagas_maximas: e.target.value }))} fullWidth inputProps={{ min: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={salvar} variant="contained" disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        titulo="Deletar evento"
        mensagem={`Deletar "${confirmDel?.titulo}"? A escala deste evento também será removida.`}
        onConfirm={() => deletar(confirmDel.id)}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}

// ─── Aba: Voluntários ────────────────────────────────────────────────────────

function TabVoluntarios({ departamentos }) {
  const [voluntarios, setVoluntarios] = useState([]);
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroDepto, setFiltroDepto] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: '', telefone: '', email: '', habilidades: '', observacoes: '', membro_id: '', departamentos: [] });
  const [editId, setEditId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const carregarMembros = useCallback(async () => {
    try {
      const res = await api.get('/secretaria/membros');
      setMembros(res.data);
    } catch { setMembros([]); }
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroDepto ? { departamento_id: filtroDepto } : {};
      const res = await api.get('/voluntariado/voluntarios', { params });
      setVoluntarios(res.data);
    } catch { setVoluntarios([]); } finally { setLoading(false); }
  }, [filtroDepto]);

  useEffect(() => { 
    carregarMembros();
    carregar(); 
  }, [carregar, carregarMembros]);

  const abrirNovo = () => {
    setForm({ nome: '', telefone: '', email: '', habilidades: '', observacoes: '', membro_id: '', departamentos: [] });
    setEditId(null); setErro(''); setOpen(true);
  };

  const abrirEditar = (v) => {
    setForm({ nome: v.nome, telefone: v.telefone || '', email: v.email || '', habilidades: v.habilidades || '', observacoes: v.observacoes || '', membro_id: v.membro_id || '', departamentos: v.departamentos.map(d => d.id) });
    setEditId(v.id); setErro(''); setOpen(true);
  };

  const toggleDepto = (id) => {
    setForm(f => ({ ...f, departamentos: f.departamentos.includes(id) ? f.departamentos.filter(x => x !== id) : [...f.departamentos, id] }));
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/voluntariado/voluntarios/${editId}`, form);
      else await api.post('/voluntariado/voluntarios', form);
      setOpen(false);
      carregar();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro ao salvar.'); }
    finally { setSaving(false); }
  };

  const deletar = async (id) => {
    try { await api.delete(`/voluntariado/voluntarios/${id}`); carregar(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro.'); }
    setConfirmDel(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Voluntários</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Departamento</InputLabel>
            <Select value={filtroDepto} label="Departamento" onChange={e => setFiltroDepto(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={abrirNovo}>Novo voluntário</Button>
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Departamentos</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Habilidades</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Eventos confirmados</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {voluntarios.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{v.nome}</TableCell>
                  <TableCell>{v.telefone || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {v.departamentos.map(d => (
                        <Chip key={d.id} label={d.nome} size="small" sx={{ bgcolor: d.cor, color: '#fff', fontWeight: 600 }} />
                      ))}
                      {v.departamentos.length === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{v.habilidades || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={v.total_eventos_confirmados} color={v.total_eventos_confirmados > 0 ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Deletar"><IconButton size="small" color="error" onClick={() => setConfirmDel(v)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {voluntarios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Alert severity="info" sx={{ my: 1 }}>Nenhum voluntário cadastrado.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog form */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Editar voluntário' : 'Novo voluntário'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField label="Nome *" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Membro da instituição *</InputLabel>
            <Select 
              value={form.membro_id || ''} 
              label="Membro da instituição *"
              onChange={e => setForm(f => ({ ...f, membro_id: e.target.value }))}
            >
              <MenuItem value="">Selecione um membro</MenuItem>
              {membros.map(m => <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Telefone" value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} fullWidth />
            <TextField label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
          </Box>
          <TextField label="Habilidades / tags" value={form.habilidades} onChange={e => setForm(f => ({ ...f, habilidades: e.target.value }))} fullWidth placeholder="ex: canta, toca violão, tem carro..." />
          <TextField label="Observações" value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} fullWidth multiline rows={2} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Departamentos</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {departamentos.map(d => (
              <Chip
                key={d.id}
                label={d.nome}
                onClick={() => toggleDepto(d.id)}
                variant={form.departamentos.includes(d.id) ? 'filled' : 'outlined'}
                sx={form.departamentos.includes(d.id) ? { bgcolor: d.cor, color: '#fff', fontWeight: 700 } : {}}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={salvar} variant="contained" disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        titulo="Deletar voluntário"
        mensagem={`Deletar "${confirmDel?.nome}"? Ele será removido de todas as escalas.`}
        onConfirm={() => deletar(confirmDel.id)}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}

// ─── Dialog: Escala de um evento ─────────────────────────────────────────────

function DialogEscala({ evento, voluntarios, open, onClose }) {
  const [escala, setEscala] = useState([]);
  const [loading, setLoading] = useState(false);
  const [volSelecionado, setVolSelecionado] = useState('');
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    if (!evento) return;
    setLoading(true);
    try {
      const res = await api.get(`/voluntariado/eventos/${evento.id}/escala`);
      setEscala(res.data.escala || []);
    } catch { setEscala([]); } finally { setLoading(false); }
  }, [evento]);

  useEffect(() => { if (open) carregar(); }, [open, carregar]);

  const adicionar = async () => {
    if (!volSelecionado) return;
    setSaving(true); setErro('');
    try {
      await api.post(`/voluntariado/eventos/${evento.id}/escala`, { voluntario_id: parseInt(volSelecionado), status: 'confirmado' });
      setVolSelecionado('');
      carregar();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro.'); }
    finally { setSaving(false); }
  };

  const alterarStatus = async (escalaId, novoStatus) => {
    try {
      await api.patch(`/voluntariado/escala/${escalaId}/status`, { status: novoStatus });
      carregar();
    } catch { }
  };

  const remover = async (escalaId) => {
    try { await api.delete(`/voluntariado/escala/${escalaId}`); carregar(); }
    catch { }
  };

  const idsNaEscala = new Set(escala.map(s => s.voluntario_id));
  const disponiveis = voluntarios.filter(v => !idsNaEscala.has(v.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon />
          Escala — {evento?.titulo}
        </Box>
        {evento && (
          <Typography variant="body2" color="text.secondary">
            {evento.recorrente
              ? `Todo ${DIAS_LABEL[evento.dia_semana] || evento.dia_semana}`
              : evento.data ? new Date(evento.data + 'T00:00:00').toLocaleDateString('pt-BR') : ''}
            {evento.hora_inicio ? ` · ${evento.hora_inicio}${evento.hora_fim ? ` – ${evento.hora_fim}` : ''}` : ''}
            {evento.vagas_maximas ? ` · ${escala.filter(s => s.status !== 'liberado').length}/${evento.vagas_maximas} vagas` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Adicionar voluntário */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <InputLabel>Adicionar voluntário</InputLabel>
            <Select value={volSelecionado} label="Adicionar voluntário" onChange={e => setVolSelecionado(e.target.value)}>
              {disponiveis.map(v => <MenuItem key={v.id} value={v.id}>{v.nome}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={adicionar} disabled={!volSelecionado || saving} startIcon={<PersonAddIcon />}>
            Adicionar
          </Button>
        </Box>

        {erro && <Alert severity="error" sx={{ mb: 1 }}>{erro}</Alert>}
        <Divider sx={{ mb: 2 }} />

        {loading ? <CircularProgress /> : (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Voluntário</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {escala.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{s.nome}</TableCell>
                    <TableCell>{s.telefone || '—'}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={s.status}
                        onChange={e => alterarStatus(s.id, e.target.value)}
                        renderValue={(v) => <Chip label={STATUS_LABEL[v]} color={STATUS_COLOR[v]} size="small" />}
                        sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                      >
                        {Object.entries(STATUS_LABEL).map(([k, lbl]) => (
                          <MenuItem key={k} value={k}><Chip label={lbl} color={STATUS_COLOR[k]} size="small" /></MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remover da escala">
                        <IconButton size="small" color="error" onClick={() => remover(s.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {escala.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Nenhum voluntário escalado ainda.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────

export default function GestaoVoluntariado({ onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [departamentos, setDepartamentos] = useState([]);
  const [voluntarios, setVoluntarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventoEscala, setEventoEscala] = useState(null);

  const carregarDepartamentos = useCallback(async () => {
    try {
      const res = await api.get('/voluntariado/departamentos');
      setDepartamentos(res.data);
    } catch { setDepartamentos([]); }
  }, []);

  const carregarVoluntarios = useCallback(async () => {
    try {
      const res = await api.get('/voluntariado/voluntarios');
      setVoluntarios(res.data);
    } catch { setVoluntarios([]); }
  }, []);

  useEffect(() => {
    Promise.all([carregarDepartamentos(), carregarVoluntarios()])
      .finally(() => setLoading(false));
  }, [carregarDepartamentos, carregarVoluntarios]);

  return (
    <>
      <Header onLogout={onLogout} />
      <ContextNavBar context="voluntariado" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>

        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/voluntariado')} size="small">Voltar</Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ bgcolor: '#00C48C', color: '#fff', p: 1.5, borderRadius: 2, display: 'flex' }}>
            <PeopleIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Voluntariado</Typography>
            <Typography variant="body1" color="text.secondary">Gerencie departamentos, agenda de eventos e escalas de voluntários.</Typography>
          </Box>
        </Box>

        {/* Resumo rápido */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 3, flexWrap: 'wrap', flexDirection: { xs: 'column', md: 'row' } }}>
          <Chip icon={<CategoryIcon />} label={`${departamentos.length} departamento(s)`} sx={{ fontWeight: 700 }} />
          <Chip icon={<PeopleIcon />} label={`${voluntarios.length} voluntário(s)`} color="primary" sx={{ fontWeight: 700 }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }} variant={window.innerWidth < 600 ? 'scrollable' : 'standard'} scrollButtons="auto">
              <Tab icon={<CategoryIcon />} iconPosition="start" label="Departamentos" />
              <Tab icon={<EventIcon />} iconPosition="start" label="Agenda" />
              <Tab
                icon={
                  <Badge badgeContent={voluntarios.length} color="primary" max={99}>
                    <PeopleIcon />
                  </Badge>
                }
                iconPosition="start"
                label="Voluntários"
              />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <TabDepartamentos departamentos={departamentos} onRefresh={carregarDepartamentos} />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <TabAgenda departamentos={departamentos} onAbrirEscala={setEventoEscala} />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <TabVoluntarios departamentos={departamentos} />
            </TabPanel>
          </>
        )}

        {/* Dialog de Escala */}
        <DialogEscala
          evento={eventoEscala}
          voluntarios={voluntarios}
          open={!!eventoEscala}
          onClose={() => setEventoEscala(null)}
        />
      </Container>
    <Footer />
    </>
  );
}
